import { Container, Grid, useMediaQuery, useTheme } from '@mui/material';
import { Redirect } from 'react-router-dom';
import { createPath, ProjectPathParams, Paths } from '../../../app/router/app.router';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { LoadingIndicator } from '../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { UserDetailsPageHeader } from '../../../ui/components/pure/page-headers/details/user-details/user-details-page-header.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { UserDeliveriesList } from './components/user-deliveries-list/user-deliveries-list.component';
import { UserDetailsActionsObserver } from './user-details-actions-observer.component';
import { UserDetailsActions } from './actions/user-details.actions';
import { useUserDetails } from './hooks/use-user-details.hook';
import { makeUserDetailsHeaderProps } from './logic/user-details-props.map';
import { useUserNotes } from './hooks/user-notes-card.hook';
import { KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { NotesCard } from '../../../ui/components/pure/notes-card/notes-card.component';
import { useUserLabels } from '../../../hooks/use-entity-labels.hook';

export function UserDetailsPage({
  params
}: {
  params: ProjectPathParams<Paths.customer> & Partial<ProjectPathParams<Paths.customerOfChildProject>>;
}) {
  const {
    user,
    retailer,
    isCourier,
    projectModules,
    deliveries,
    deliveriesInfiniteScroll,
    formState,
    onFormInput,
    actions
  } = useUserDetails(params.projectID, params.userID, params.childProjectID);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const notesProps = useUserNotes({ sortOrder: KnownSortOrder.Desc }, params.userID, params.projectID);
  // The error and empty branches run before the user has loaded, so the wording there comes from
  // the project type rather than the user type — carrier and region projects resolve to "Courier".
  const userLabel = useUserLabels(params.projectID).singular;

  // Couriers have their own page, but still reach this route from older links and search results.
  if (isCourier && user.data) {
    return (
      <Redirect
        to={createPath(Paths.courier, {
          projectID: params.shortcode || params.projectID,
          courierID: user.data.id
        })}
      />
    );
  }

  const error = user.error || retailer.error;

  if (error) {
    const entity = retailer.error ? 'project' : userLabel.toLowerCase();
    if (error.statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if (error.statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" entity={entity} />;
    }

    if (error.statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" entity={entity} />;
    }
    return <ErrorFullPage />;
  }

  if (user.isLoading || retailer.isLoading || projectModules.isLoading) {
    return <LoadingIndicator fullPage />;
  }

  if (!user.data || !retailer.data) {
    return <ErrorFullPage preset="NOT_FOUND" entity={userLabel.toLowerCase()} />;
  }

  const headerProps = makeUserDetailsHeaderProps(
    user.data,
    retailer.data,
    projectModules.data || [],
    actions.setAction,
    userLabel
  );

  return (
    <Container>
      <WindowTitle detail={user.data?.displayName || 'Loading'} pageTitle="Users" />
      <UserDetailsPageHeader {...headerProps} />
      {!user.isLoading && user.data !== undefined && (
        <UserDetailsActionsObserver
          user={user.data}
          actions={actions}
          projectID={params.childProjectID || params.projectID}
        />
      )}
      <Grid
        container
        sx={{ flexDirection: isMobile ? 'column-reverse' : 'row' }}
        spacing={1}
        style={{ display: 'flex' }}
      >
        <Grid
          size={{
            xs: 12,
            lg: 8
          }}
        >
          <UserDeliveriesList
            params={params}
            pages={deliveries.data?.pages || []}
            infiniteScroll={deliveriesInfiniteScroll}
            isLoading={deliveries.isLoading}
            isPreviousData={deliveries.isPreviousData}
            formState={formState}
            onFormInput={onFormInput}
            hasError={deliveries.isError}
            isCourier={false}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            lg: 4
          }}
        >
          <NotesCard
            onClick={notesProps.handlers.onLoadMoreNotes}
            onAddNote={() => actions.setAction(UserDetailsActions.ADD_NOTE)}
            {...notesProps}
          />
        </Grid>
      </Grid>
    </Container>
  );
}
